<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import BulkSelectBar from 'dashboard/components-next/ui/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  visibleCompanyIds: {
    type: Array,
    default: () => [],
  },
  selectedCompanyIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'clearSelection',
  'toggleAll',
  'deleteSelected',
  'exportSelected',
]);

const { t } = useI18n();

const selectedCount = computed(() => props.selectedCompanyIds.length);
const totalVisibleCompanies = computed(() => props.visibleCompanyIds.length);

const selectAllLabel = computed(() => {
  if (!totalVisibleCompanies.value) {
    return '';
  }

  return t('COMPANIES_BULK_ACTIONS.SELECT_ALL', {
    count: totalVisibleCompanies.value,
  });
});

const selectedCountLabel = computed(() =>
  t('COMPANIES_BULK_ACTIONS.SELECTED_COUNT', {
    count: selectedCount.value,
  })
);

const allItems = computed(() =>
  props.visibleCompanyIds.map(id => ({
    id,
  }))
);

const selectionModel = computed({
  get: () => new Set(props.selectedCompanyIds),
  set: newSet => {
    if (!props.visibleCompanyIds.length) {
      emit('toggleAll', false);
      return;
    }

    const shouldSelectAll =
      newSet.size === props.visibleCompanyIds.length && newSet.size > 0;
    emit('toggleAll', shouldSelectAll);
  },
});

const emitClearSelection = () => {
  emit('clearSelection');
};
</script>

<template>
  <div
    class="sticky top-0 z-10 bg-gradient-to-b from-n-background from-90% to-transparent px-6 pt-1 pb-2"
  >
    <BulkSelectBar
      v-model="selectionModel"
      :all-items="allItems"
      :select-all-label="selectAllLabel"
      :selected-count-label="selectedCountLabel"
      class="py-2 ltr:!pr-3 rtl:!pl-3 justify-between"
    >
      <template #secondary-actions>
        <Button
          sm
          ghost
          slate
          :label="t('COMPANIES_BULK_ACTIONS.CLEAR_SELECTION')"
          class="!px-1.5"
          @click="emitClearSelection"
        />
      </template>
      <template #actions>
        <div class="flex items-center gap-2 ml-auto">
          <Button
            sm
            faded
            slate
            icon="i-lucide-download"
            :label="t('COMPANIES_BULK_ACTIONS.EXPORT')"
            :disabled="!selectedCount || isLoading"
            class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
            @click="emit('exportSelected')"
          />
          <Policy :permissions="['administrator']">
            <Button
              v-tooltip.bottom="t('COMPANIES_BULK_ACTIONS.DELETE_COMPANIES')"
              sm
              faded
              ruby
              icon="i-lucide-trash"
              :label="t('COMPANIES_BULK_ACTIONS.DELETE_COMPANIES')"
              :aria-label="t('COMPANIES_BULK_ACTIONS.DELETE_COMPANIES')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="!px-1.5 [&>span:nth-child(2)]:hidden"
              @click="emit('deleteSelected')"
            />
          </Policy>
        </div>
      </template>
    </BulkSelectBar>
  </div>
</template>
